import { useSyncExternalStore } from "react";
import type { AuthTokenPair, LoginInput } from "@/lib/api/authRequests";
import * as authApi from "@/lib/api/authRequests";
import { ApiError } from "@/lib/api/errors";
import { decodeAccessToken, msUntilExpiry, type TokenClaims } from "@/lib/api/jwt";
import { knownRoles, type Persona, personaFromRoles, type RoleName } from "@/lib/api/roles";

// Access token lives in memory only. The refresh token is mirrored to
// localStorage so sessions survive reloads (documented XSS trade-off; the
// hardening path is an httpOnly refresh cookie set by the backend).
const REFRESH_STORAGE_KEY = "nextsub.refresh_token";
// User id of the session that owns the stored refresh token. Lets other tabs
// tell "same session, rotated" apart from "someone else signed in".
const SESSION_USER_STORAGE_KEY = "nextsub.session_user";
// Lineage id of the stored refresh-token chain: created at login, kept across
// rotations. Lets logout tell "my session, rotated" from "another login".
const SESSION_ID_STORAGE_KEY = "nextsub.session_id";
let sessionLineage: string | null = null;

function readStoredSessionId(): string | null {
  try {
    return localStorage.getItem(SESSION_ID_STORAGE_KEY);
  } catch {
    return null;
  }
}

function newLineageId(): string {
  try {
    return crypto.randomUUID();
  } catch {
    return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
  }
}

// Refresh this many ms before the access token actually expires.
export const REFRESH_SKEW_MS = 60_000;

export type SessionStatus = "loading" | "authenticated" | "anonymous";

export interface MeProfile {
  user_id: string;
  tenant_id: string;
  roles: string[];
  email?: string;
  display_name?: string;
  status?: string;
}

export interface SessionSnapshot {
  /** Authenticated-but-roles-not-hydrated presents as "loading". */
  status: SessionStatus;
  accessToken: string | null;
  refreshToken: string | null;
  claims: TokenClaims | null;
  roles: RoleName[];
  persona: Persona;
  me: MeProfile | null;
  tenantId: string | null;
  userId: string | null;
}

function readStoredRefresh(): string | null {
  try {
    return localStorage.getItem(REFRESH_STORAGE_KEY);
  } catch {
    return null;
  }
}

function readStoredSessionUser(): string | null {
  try {
    return localStorage.getItem(SESSION_USER_STORAGE_KEY);
  } catch {
    return null;
  }
}

// The token whose write this tab rolled back (storage left empty), and when:
// a later rotation spending it may store its replacement, unless someone
// signed out since (storage is empty for that reason too).
let rolledBack: { token: string; at: number } | null = null;

function writeStoredRefresh(token: string | null, userId?: string | null, lineage?: string): void {
  rolledBack = null;
  try {
    if (token && lineage) localStorage.setItem(SESSION_ID_STORAGE_KEY, lineage);
    else if (!token) localStorage.removeItem(SESSION_ID_STORAGE_KEY);
    // User first: a tab reacting to the token change must see the matching owner.
    if (token && userId) localStorage.setItem(SESSION_USER_STORAGE_KEY, userId);
    else if (!token) localStorage.removeItem(SESSION_USER_STORAGE_KEY);
    if (token) localStorage.setItem(REFRESH_STORAGE_KEY, token);
    else localStorage.removeItem(REFRESH_STORAGE_KEY);
  } catch {
    // Storage unavailable (private mode) or a write failed part-way: the
    // session is memory-only. Never leave a mix behind (e.g. the previous
    // login's token under this login's lineage/owner): clear what can be.
    if (token) {
      rolledBack = { token, at: Date.now() };
      for (const key of [REFRESH_STORAGE_KEY, SESSION_USER_STORAGE_KEY, SESSION_ID_STORAGE_KEY]) {
        try {
          localStorage.removeItem(key);
        } catch {
          /* nothing more to do */
        }
      }
    }
  }
}

interface InternalState {
  status: SessionStatus;
  accessToken: string | null;
  refreshToken: string | null;
  claims: TokenClaims | null;
  roles: RoleName[];
  rolesHydrated: boolean;
  me: MeProfile | null;
}

const initialRefresh = readStoredRefresh();
// Whose session this tab last had (or found stored at start): a return link
// left when it ends belongs to that user only.
let lastSessionUser: string | null = initialRefresh ? readStoredSessionUser() : null;
let state: InternalState = {
  status: initialRefresh ? "loading" : "anonymous",
  accessToken: null,
  refreshToken: initialRefresh,
  claims: null,
  roles: [],
  rolesHydrated: false,
  me: null,
};

let snapshot: SessionSnapshot = buildSnapshot(state);
const listeners = new Set<() => void>();

function buildSnapshot(s: InternalState): SessionSnapshot {
  return {
    status: s.status === "authenticated" && !s.rolesHydrated ? "loading" : s.status,
    accessToken: s.accessToken,
    refreshToken: s.refreshToken,
    claims: s.claims,
    roles: s.roles,
    persona: personaFromRoles(s.roles),
    me: s.me,
    tenantId: s.claims?.tenantId ?? null,
    userId: s.claims?.userId ?? null,
  };
}

function setState(patch: Partial<InternalState>) {
  state = { ...state, ...patch };
  snapshot = buildSnapshot(state);
  for (const listener of listeners) listener();
}

export function getSessionSnapshot(): SessionSnapshot {
  return snapshot;
}

export function subscribeSession(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function useSession(): SessionSnapshot {
  return useSyncExternalStore(subscribeSession, getSessionSnapshot, getSessionSnapshot);
}

// Hooks registered by the app (query cache clearing, /me fetch). Kept as late
// bindings so this module stays free of React Query / generated-client imports
// and unit-testable in isolation.
let clearCaches: () => void = () => {};
// `accessToken` is passed while a refresh is applying new tokens: the call
// must carry that token explicitly so it bypasses the request layer's
// "wait for the in-flight refresh" gate (which would wait on itself).
let fetchMe: (accessToken?: string) => Promise<MeProfile> = async () => {
  throw new Error("session: fetchMe not configured");
};

export function configureSession(deps: {
  clearCaches: () => void;
  fetchMe: (accessToken?: string) => Promise<MeProfile>;
}) {
  clearCaches = deps.clearCaches;
  fetchMe = deps.fetchMe;
}

let refreshTimer: ReturnType<typeof setTimeout> | null = null;
// Session generation: bumped on every login, logout and forced clear. Async
// work (bootstrap, silent refresh, profile hydration) captures it at start and
// drops its result if the session changed underneath it.
let generation = 0;
let bootstrapPromise: Promise<void> | null = null;
// Set by an explicit sign-out; cleared when a session starts. Lets the app
// shell tell "you signed out" apart from "your session ended".
let signedOutVoluntarily = false;
// Written by logout() just before it removes the refresh token, so other tabs
// can tell a deliberate sign-out from a session that ended on its own.
const SIGNED_OUT_KEY = "nextsub.signed_out_at";
const SIGNED_OUT_WINDOW_MS = 10_000;

function markSignedOutForOtherTabs() {
  try {
    localStorage.setItem(SIGNED_OUT_KEY, String(Date.now()));
  } catch {
    /* storage unavailable */
  }
}

function signedOutAt(): number {
  try {
    return Number(localStorage.getItem(SIGNED_OUT_KEY)) || 0;
  } catch {
    return 0;
  }
}

function signedOutRecentlyElsewhere(): boolean {
  try {
    const at = Number(localStorage.getItem(SIGNED_OUT_KEY));
    return !!at && Date.now() - at < SIGNED_OUT_WINDOW_MS;
  } catch {
    return false;
  }
}

const PROFILE_RETRY_DELAYS_MS = [1_000, 3_000];
// Never schedule a refresh sooner than this, whatever the clock says.
export const MIN_REFRESH_DELAY_MS = 5_000;
// Backoff for refreshes that fail for a transient reason (offline, 5xx, 429).
const TRANSIENT_RETRY_DELAYS_MS = [2_000, 5_000, 15_000, 30_000];
// Access-token expiry on this client's clock, from expires_in at receipt.
// Comparing the JWT `exp` with Date.now() breaks when the device clock is off.
let accessExpiresAt: number | null = null;
const REFRESH_LOCK = "nextsub.refresh";

function clearRefreshTimer() {
  if (refreshTimer) {
    clearTimeout(refreshTimer);
    refreshTimer = null;
  }
}

/**
 * Put tokens in memory. Storage is written elsewhere, inside the cross-tab
 * lock: by persistLogin for a login and by rotateTokens for a refresh.
 */
function setTokens(tokens: AuthTokenPair) {
  signedOutVoluntarily = false;
  lastSessionUser = decodeAccessToken(tokens.access_token)?.userId ?? lastSessionUser;
  retiredAccessToken = null;
  accessExpiresAt =
    typeof tokens.expires_in === "number" && tokens.expires_in > 0
      ? Date.now() + tokens.expires_in * 1000
      : null;
  setState({
    status: "authenticated",
    accessToken: tokens.access_token,
    refreshToken: tokens.refresh_token,
    claims: decodeAccessToken(tokens.access_token),
  });
}

function resetToAnonymous({ keepStoredToken = false } = {}) {
  clearRefreshTimer();
  accessExpiresAt = null;
  if (!keepStoredToken) writeStoredRefresh(null);
  setState({
    status: "anonymous",
    accessToken: null,
    refreshToken: null,
    claims: null,
    roles: [],
    rolesHydrated: false,
    me: null,
  });
}

/**
 * Remove the stored session only if it is still this one, checked and done
 * under the refresh lock: another tab may store a new login between a check
 * outside the lock and the delete. Local state is cleared by the caller.
 */
function clearStoredSessionIfOurs(isOurs: (stored: string, storedLineage: string | null) => boolean) {
  void withRefreshLockBounded(() => {
    const stored = readStoredRefresh();
    if (stored && isOurs(stored, readStoredSessionId())) writeStoredRefresh(null);
  });
}

// The session generation the server itself ended (a rejected refresh or a 401
// for the current token), as opposed to a sign-in/out in another tab: callers
// waiting on a write can tell "revoked" from "superseded".
let revokedGeneration: number | null = null;

/** Was the session of generation `gen` ended by the server (revoked)? */
function wasRevoked(gen: number): boolean {
  return revokedGeneration === gen && state.status === "anonymous";
}

/** Force-clear an authenticated session (logout, rejected refresh). */
function clearAuthenticatedState() {
  revokedGeneration = generation;
  const lineage = sessionLineage;
  const mine = [state.refreshToken, lastRotatedByThisTab];
  generation += 1;
  bootstrapPromise = null;
  clearCaches();
  resetToAnonymous({ keepStoredToken: true });
  clearStoredSessionIfOurs((stored, storedLineage) =>
    storedLineage ? storedLineage === lineage : mine.includes(stored),
  );
}

/**
 * A request came back 401. Only a 401 for the access token this session holds
 * right now ends the session; a late 401 from a token that silent refresh or a
 * new login has already replaced is ignored.
 */
function handleUnauthorized(sentAccessToken: string | null) {
  if (!sentAccessToken || sentAccessToken !== state.accessToken) return;
  // Another tab may already have stored a newer login whose storage event
  // this tab hasn't processed: switch to it instead of deleting it.
  const storedLineage = readStoredSessionId();
  if (storedLineage && storedLineage !== sessionLineage && readStoredRefresh()) {
    switchToStoredSession();
    return;
  }
  clearAuthenticatedState();
}

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

// /me during login, bootstrap and refresh must not hang them forever; a
// timeout counts as a transient failure (retried, never a sign-out).
const PROFILE_TIMEOUT_MS = 20_000;

function withDeadline<T>(promise: Promise<T>, ms: number): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(
      () => reject(new ApiError({ code: "network_error", message: "", status: 0 })),
      ms,
    );
    promise.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (err: unknown) => {
        clearTimeout(timer);
        reject(err);
      },
    );
  });
}

/** /me with a short retry: a blip right after a token rotation must not strand the session. */
async function fetchProfileWithRetry(gen: number, accessToken: string): Promise<MeProfile> {
  for (let attempt = 0; ; attempt++) {
    try {
      return await withDeadline(fetchMe(accessToken), PROFILE_TIMEOUT_MS);
    } catch (err) {
      const unauthorized = err instanceof ApiError && err.code === "unauthorized";
      if (unauthorized || gen !== generation || attempt >= PROFILE_RETRY_DELAYS_MS.length) throw err;
      await wait(PROFILE_RETRY_DELAYS_MS[attempt]);
    }
  }
}

/** Store tokens and hydrate roles. Returns false if the session changed meanwhile. */
async function applyTokens(tokens: AuthTokenPair, gen: number, { rotation = false } = {}): Promise<boolean> {
  if (gen !== generation) return false;
  const previousUser = state.claims?.userId;
  const nextUser = decodeAccessToken(tokens.access_token)?.userId;
  // The token belongs to someone else (a login stored by another tab or app
  // that this tab picked up): that is a new session, not a rotation. Start a
  // new generation so the previous user's in-flight responses are dropped,
  // and don't show the previous user's roles meanwhile. (Login and bootstrap
  // replace the session on purpose and track their own generation.)
  const switched = rotation && !!previousUser && nextUser !== previousUser;
  if (switched) {
    generation += 1;
    gen = generation;
    bootstrapPromise = null;
    clearCaches();
    setState({ roles: [], rolesHydrated: false, me: null });
  } else if (previousUser && nextUser !== previousUser) {
    clearCaches();
  }
  setTokens(tokens);
  let me: MeProfile;
  try {
    me = await fetchProfileWithRetry(gen, tokens.access_token);
  } catch (err) {
    // The caller's generation is gone after a switch, so it can't settle
    // this: never leave the new user's session half-loaded. Restore it from
    // storage like a page load (bootstrap retries transient failures and
    // parks the token if it keeps failing), instead of reading as signed out.
    if (switched && gen === generation) {
      generation += 1;
      bootstrapPromise = null;
      clearRefreshTimer();
      accessExpiresAt = null;
      sessionLineage = readStoredSessionId();
      setState({
        status: "loading",
        refreshToken: readStoredRefresh() ?? tokens.refresh_token,
        accessToken: null,
        claims: null,
        roles: [],
        rolesHydrated: false,
        me: null,
      });
      void bootstrap();
    }
    throw err;
  }
  if (gen !== generation) return false;
  setState({ roles: knownRoles(me.roles ?? []), rolesHydrated: true, me });
  return true;
}

/**
 * Refresh tokens are single-use and shared by every tab through localStorage.
 * Serialize refreshes across tabs (Web Locks when available) and always spend
 * the newest stored token, so one tab's rotation never strands another.
 */
/** Storage now holds another login's token chain; this tab must switch, not spend it. */
class StoredSessionChanged extends Error {}

// Refresh tokens this tab has spent (single-use): never offered again even if
// storage still shows one because persisting its replacement failed.
const spentByThisTab = new Set<string>();

async function rotateTokens(
  fallback: string,
  expectLineage?: string | null,
): Promise<{ tokens: AuthTokenPair; spent: string }> {
  const run = async () => {
    // Checked inside the lock: a new login stored by another tab must never be
    // spent (and installed) under this tab's current session generation.
    const storedLineage = readStoredSessionId();
    if (expectLineage !== undefined && storedLineage && storedLineage !== expectLineage) {
      throw new StoredSessionChanged();
    }
    // The newest stored token, unless this tab already spent it (storing its
    // replacement failed, e.g. storage full): then the in-memory replacement
    // is the live one, and spending the old token again would end the session.
    const stored = readStoredRefresh();
    const spent = stored && !spentByThisTab.has(stored) ? stored : fallback;
    const tokens = await authApi.refresh(spent);
    spentByThisTab.add(spent);
    // A logout gave up waiting for this lock and signed the chain out while
    // the refresh was on the wire: never store the replacement, and revoke it
    // so the signed-out session can't come back.
    if (signedOutChains.has(spent)) {
      void authApi.logout(tokens.refresh_token).catch(() => {});
      return { tokens, spent };
    }
    // Store the rotated token before releasing the lock, so the next tab to
    // take it reads the new token instead of spending the consumed one.
    // Persist whenever we spent the token storage still holds: the replacement
    // is the only live link in the chain, even if this tab's own session
    // moved on meanwhile. If storage changed (signed out, or another tab
    // already rotated), leave it.
    // Also when storage still shows a token this tab already spent (an
    // earlier write failed): that stale copy is this login's, so repairing it
    // is safe, and without it a reload would spend a revoked token. Another
    // login's token (a different lineage) is never overwritten.
    const storedNow = readStoredRefresh();
    const sameLogin =
      expectLineage === undefined || !readStoredSessionId() || readStoredSessionId() === expectLineage;
    // Or storage is empty only because this tab rolled back the write of the
    // token just spent (and nobody signed out since).
    const refill = storedNow === null && rolledBack?.token === spent && signedOutAt() <= rolledBack.at;
    if (storedNow === spent || refill || (storedNow !== null && spentByThisTab.has(storedNow) && sameLogin)) {
      // A refill also restores the lineage the rollback removed.
      writeStoredRefresh(
        tokens.refresh_token,
        decodeAccessToken(tokens.access_token)?.userId ?? null,
        refill ? (expectLineage ?? undefined) : undefined,
      );
      lastRotatedByThisTab = tokens.refresh_token;
    }
    return { tokens, spent };
  };
  const locks = typeof navigator !== "undefined" ? navigator.locks : undefined;
  return locks ? locks.request(REFRESH_LOCK, run) : run();
}

/**
 * A refresh that failed without the server rejecting the token (offline,
 * server error, rate limit). The refresh token is still good; keep it.
 */
function isTransientRefreshFailure(err: unknown): boolean {
  if (!(err instanceof ApiError)) return true;
  return (
    err.code === "network_error" || err.code === "rate_limited" || err.status === 429 || err.status >= 500
  );
}

/** Failed refresh: end the session, but leave storage alone if another tab already rotated it. */
// The refresh token this tab last wrote while rotating. A rejected session
// must not leave it behind just because it differs from the token spent.
let lastRotatedByThisTab: string | null = null;
// Refresh tokens of chains this tab signed out (see logout).
const signedOutChains = new Set<string>();
// The access token a cross-tab session switch made unusable; cleared when new
// tokens are installed.
let retiredAccessToken: string | null = null;

function onRefreshFailed(spent: string | null, gen: number) {
  if (gen !== generation) return;
  revokedGeneration = generation;
  const rotated = lastRotatedByThisTab;
  generation += 1;
  bootstrapPromise = null;
  clearCaches();
  resetToAnonymous({ keepStoredToken: true });
  lastRotatedByThisTab = null;
  // Keep storage when it holds a third token another tab wrote (checked
  // under the lock, so a login stored meanwhile is never deleted).
  clearStoredSessionIfOurs((stored) => stored === spent || (rotated !== null && stored === rotated));
}

/** Delay until the next silent refresh: ahead of expiry, never in a tight loop. */
export function refreshDelayMs(now = Date.now()): number {
  let remaining: number;
  let lifetime: number | null = null;
  if (accessExpiresAt !== null) {
    remaining = accessExpiresAt - now;
  } else if (state.claims) {
    remaining = msUntilExpiry(state.claims, now);
  } else {
    return MIN_REFRESH_DELAY_MS;
  }
  if (accessExpiresAt !== null) lifetime = remaining;
  // Short-lived tokens refresh at half their life rather than a fixed skew.
  const lead = lifetime !== null && lifetime < 2 * REFRESH_SKEW_MS ? lifetime / 2 : REFRESH_SKEW_MS;
  return Math.max(MIN_REFRESH_DELAY_MS, remaining - lead);
}

// One refresh at a time per tab (the Web Lock serializes across tabs). Silent
// refresh, the pre-send expiry check and 401 recovery all share it.
let inflightRefresh: Promise<boolean> | null = null;

/**
 * Rotate tokens now. Resolves true when a fresh access token is in place.
 * A rejected refresh ends the session; a transient failure keeps the token
 * and schedules a retry with backoff.
 */
function runRefresh(attempt = 0): Promise<boolean> {
  if (inflightRefresh) return inflightRefresh;
  if (state.status !== "authenticated" || !state.refreshToken) return Promise.resolve(false);
  clearRefreshTimer();
  const gen = generation;
  const tokenAtStart = state.refreshToken;
  let spent: string | null = null;
  inflightRefresh = rotateTokens(tokenAtStart, sessionLineage)
    .then(async (result) => {
      spent = result.spent;
      const applied = await applyTokens(result.tokens, gen, { rotation: true });
      if (applied) scheduleSilentRefresh();
      return applied;
    })
    .catch((err: unknown) => {
      if (gen !== generation) return false;
      if (err instanceof StoredSessionChanged) {
        switchToStoredSession();
        return false;
      }
      if (isTransientRefreshFailure(err)) {
        const next = TRANSIENT_RETRY_DELAYS_MS[Math.min(attempt, TRANSIENT_RETRY_DELAYS_MS.length - 1)];
        scheduleSilentRefresh(next, attempt + 1);
        return false;
      }
      onRefreshFailed(spent ?? tokenAtStart, gen);
      return false;
    });
  const current = inflightRefresh;
  // Only clear the slot if it still holds this refresh (a session switch may
  // have installed its own follow-up refresh meanwhile).
  void current.finally(() => {
    if (inflightRefresh === current) inflightRefresh = null;
  });
  return current;
}

// Silent refresh shortly before access-token expiry.
function scheduleSilentRefresh(delay = refreshDelayMs(), attempt = 0) {
  clearRefreshTimer();
  if (state.status !== "authenticated" || !state.claims || !state.refreshToken) return;
  refreshTimer = setTimeout(() => {
    refreshTimer = null;
    void runRefresh(attempt);
  }, delay);
}

// Treat the access token as expired this long before it actually is, so a
// request never leaves with a token that dies in flight.
const ACCESS_EXPIRY_MARGIN_MS = 5_000;

function accessTokenExpiring(now = Date.now()): boolean {
  if (accessExpiresAt !== null) return accessExpiresAt - now <= ACCESS_EXPIRY_MARGIN_MS;
  return state.claims ? msUntilExpiry(state.claims, now) <= ACCESS_EXPIRY_MARGIN_MS : false;
}

/**
 * Called before every API request. After a laptop sleep or a throttled
 * background timer the access token may already be dead; refresh first
 * (shared with any refresh already running) instead of sending it.
 */
async function ensureFreshAccessToken(): Promise<void> {
  if (inflightRefresh) await inflightRefresh;
  else if (state.status === "authenticated" && accessTokenExpiring()) await runRefresh();
  // A session switch retired the previous login's access token. If the
  // follow-up refresh failed (offline, 5xx, 429), try once more, and never
  // send the retired token: its responses would land in the new session.
  if (retiredAccessToken && state.accessToken === retiredAccessToken) {
    if (state.status === "authenticated") await runRefresh();
    if (state.accessToken === retiredAccessToken) {
      throw new ApiError({ code: "network_error", message: "", status: 0 });
    }
  }
  // The refresh failed transiently (offline, 5xx, 429) and the token is
  // already dead: sending it would come back 401, which reads as a definitive
  // refusal (dropping kept idempotency keys). Fail before dispatch instead,
  // as the network error it really is.
  if (state.status === "authenticated" && accessTokenExpired()) {
    throw new ApiError({ code: "network_error", message: "", status: 0 });
  }
}

function accessTokenExpired(now = Date.now()): boolean {
  if (accessExpiresAt !== null) return accessExpiresAt <= now;
  return state.claims ? msUntilExpiry(state.claims, now) <= 0 : false;
}

/** Identifies the session a request was started in (bumped on login, logout and user switch). */
function sessionGeneration(): number {
  return generation;
}

/** True when `token` is this session's current access token and not about to expire. */
function isAccessTokenUsable(token: string | null): boolean {
  return !!token && token === state.accessToken && !accessTokenExpiring();
}

/**
 * A request sent with `sentAccessToken` came back 401. Resolves true when the
 * caller should retry once with the (new) current token. Never ends the
 * session itself unless the refresh token is rejected.
 */
async function recoverFromUnauthorized(
  sentAccessToken: string | null,
  requestGeneration: number,
): Promise<boolean> {
  if (!sentAccessToken) return false;
  // A request from an earlier session (signed out, or another user since)
  // must never be replayed with the current session's token.
  if (requestGeneration !== generation) return false;
  if (inflightRefresh) await inflightRefresh;
  if (requestGeneration !== generation) return false;
  // Already replaced by a refresh or a new login: retry with the new token.
  if (state.accessToken && state.accessToken !== sentAccessToken) return state.status === "authenticated";
  if (state.status !== "authenticated" || state.accessToken !== sentAccessToken) return false;
  const ok = await runRefresh();
  // The refresh may have picked up another user's login (a new generation):
  // this request belongs to the previous session and must not be replayed.
  return ok && requestGeneration === generation;
}

/**
 * Exchange a surviving refresh token for an access token, or resolve to
 * anonymous. Idempotent; safe to call from several route guards.
 */
function bootstrap(): Promise<void> {
  if (state.status !== "loading") return Promise.resolve();
  if (bootstrapPromise) return bootstrapPromise;
  const token = state.refreshToken;
  if (!token) {
    resetToAnonymous();
    return Promise.resolve();
  }
  const gen = generation;
  sessionLineage = readStoredSessionId();
  const lineage = sessionLineage;
  const attempt = async (n: number): Promise<void> => {
    let spent: string | null = null;
    try {
      // Same lock-held lineage check as silent refresh: never spend another
      // tab's newer login under this bootstrap.
      // A retry falls back to the replacement an earlier attempt installed
      // (if storing it failed, storage still shows the spent original).
      const result = await rotateTokens(n > 0 ? (state.refreshToken ?? token) : token, lineage);
      spent = result.spent;
      if (await applyTokens(result.tokens, gen)) scheduleSilentRefresh();
    } catch (err) {
      if (gen !== generation) return;
      if (err instanceof StoredSessionChanged) {
        // Another tab signed in meanwhile: start over from its stored login.
        // An earlier attempt may already have installed tokens (it failed only
        // on the profile): back to "loading" without them, or bootstrap()
        // would return at once and leave the old token in place.
        generation += 1;
        sessionLineage = readStoredSessionId();
        clearRefreshTimer();
        accessExpiresAt = null;
        setState({
          status: "loading",
          refreshToken: readStoredRefresh(),
          accessToken: null,
          claims: null,
          roles: [],
          rolesHydrated: false,
          me: null,
        });
        bootstrapPromise = null;
        await bootstrap();
        return;
      }
      const unauthorized = err instanceof ApiError && err.code === "unauthorized";
      // The profile could not be loaded after a rotation, or the refresh
      // itself failed for a transient reason (offline, 5xx, 429).
      const transient = (spent && !unauthorized) || (!spent && isTransientRefreshFailure(err));
      if (!transient) {
        onRefreshFailed(spent ?? token, gen);
        return;
      }
      if (n < BOOTSTRAP_RETRY_DELAYS_MS.length) {
        // Stay "loading" and try again shortly (rotateTokens re-reads storage,
        // so a rotation that already happened is not spent twice).
        await wait(BOOTSTRAP_RETRY_DELAYS_MS[n]);
        if (gen !== generation) return;
        return attempt(n + 1);
      }
      // Still failing: settle as signed out but park the refresh token, so the
      // next visit to a signed-in page (resumeIfParked) or a reload recovers.
      generation += 1;
      clearCaches();
      resetToAnonymous({ keepStoredToken: true });
    }
  };
  const current: Promise<void> = attempt(0).finally(() => {
    // A restarted bootstrap may own the slot by now; leave it alone.
    if (bootstrapPromise === current) bootstrapPromise = null;
  });
  bootstrapPromise = current;
  return current;
}

const BOOTSTRAP_RETRY_DELAYS_MS = [2_000, 5_000];

/**
 * After bootstrap gave up on a transient failure the token is parked in
 * storage. Signed-in routes call this so the session resumes without a full
 * reload once the network is back. Never after a deliberate sign-out.
 */
function resumeIfParked(): void {
  if (state.status !== "anonymous" || signedOutVoluntarily) return;
  const stored = readStoredRefresh();
  if (!stored) return;
  setState({ status: "loading", refreshToken: stored });
}

/**
 * Another tab stored a different login (another user, or the same user signing
 * in again). Start a new session generation from the stored token: in-flight
 * work of the old session is dropped, caches are cleared, and a refresh starts
 * once any refresh already running has settled. For the same person the shell
 * stays mounted so an open one-time secret or draft survives.
 */
function switchToStoredSession() {
  const stored = readStoredRefresh();
  if (!stored) return;
  const owner = readStoredSessionUser();
  const otherUser = !!owner && !!state.claims?.userId && owner !== state.claims.userId;
  generation += 1;
  bootstrapPromise = null;
  clearRefreshTimer();
  clearCaches();
  sessionLineage = readStoredSessionId();
  if (otherUser) setState({ refreshToken: stored, roles: [], rolesHydrated: false, me: null });
  else setState({ refreshToken: stored });
  // Until the new login is applied, the old access token must not be used:
  // mark it expiring so every request waits for the follow-up refresh, which
  // is installed as the in-flight refresh right away.
  accessExpiresAt = Date.now() - 1;
  retiredAccessToken = state.accessToken;
  const switchGen = generation;
  const prior = inflightRefresh;
  const followUp: Promise<boolean> = (prior ?? Promise.resolve(false))
    .catch(() => false)
    .then(() => {
      // Release the slot first, even when this follow-up is obsolete, so a
      // settled promise never blocks later refreshes.
      if (inflightRefresh === followUp) inflightRefresh = null;
      if (switchGen !== generation) return false;
      return runRefresh();
    });
  inflightRefresh = followUp;
}

// Another tab rotated or removed the refresh token.
if (typeof window !== "undefined") {
  window.addEventListener("storage", (event) => {
    if (event.key !== REFRESH_STORAGE_KEY) return;
    if (event.newValue === null) {
      // Events can arrive late (background tabs). If storage holds a token
      // again, a newer sign-in already happened: this removal is obsolete and
      // its own change event follows.
      if (readStoredRefresh() !== null) return;
      if (state.status !== "anonymous") {
        const voluntary = signedOutRecentlyElsewhere();
        // The other tab already removed the token; never delete storage here.
        generation += 1;
        bootstrapPromise = null;
        clearCaches();
        resetToAnonymous({ keepStoredToken: true });
        if (voluntary) signedOutVoluntarily = true;
      }
      return;
    }
    if (state.status === "loading") {
      // Another tab signed in or rotated while this one is still
      // bootstrapping: drop the in-flight attempt and start over from the
      // token now in storage, so the old result is never installed.
      generation += 1;
      bootstrapPromise = null;
      sessionLineage = readStoredSessionId();
      setState({ refreshToken: event.newValue });
      void bootstrap();
      return;
    }
    if (state.status === "anonymous") {
      // A new sign-in in another tab: let this tab resume it on its next
      // signed-in navigation, even after an earlier deliberate sign-out here.
      if (readStoredSessionId()) signedOutVoluntarily = false;
      return;
    }
    if (state.status !== "authenticated") return;
    const owner = readStoredSessionUser();
    const storedLineage = readStoredSessionId();
    const otherUser = !!owner && !!state.claims?.userId && owner !== state.claims.userId;
    // Same user, new login (e.g. after a tenant promotion): a different
    // lineage is a new session too, with possibly different tenant and roles.
    // A stored lineage that differs from ours is a new login, including when
    // this tab has none (a session restored from the old app's storage).
    const newLogin = !!storedLineage && storedLineage !== sessionLineage;
    if (otherUser || newLogin) {
      switchToStoredSession();
      return;
    }
    sessionLineage = readStoredSessionId();
    setState({ refreshToken: event.newValue });
  });
}

/**
 * Store a new login's refresh token and lineage. Done inside the cross-tab
 * refresh lock: a refresh another tab has in flight decides whether to store
 * its rotation by checking storage, and that check and its write must not
 * straddle this write (it would store its token under this login's lineage).
 */
async function persistLogin(tokens: AuthTokenPair): Promise<void> {
  const write = () => {
    try {
      localStorage.removeItem(SIGNED_OUT_KEY);
    } catch {
      /* storage unavailable */
    }
    sessionLineage = newLineageId();
    writeStoredRefresh(
      tokens.refresh_token,
      decodeAccessToken(tokens.access_token)?.userId ?? null,
      sessionLineage,
    );
  };
  // Unbounded wait: a login must be stored before it counts as signed in
  // (a reload would otherwise lose it, or restore the previous account). The
  // lock is only ever held for a refresh, and those time out.
  const locks = typeof navigator !== "undefined" ? navigator.locks : undefined;
  if (!locks) return write();
  await locks.request(REFRESH_LOCK, async () => write());
}

/**
 * Run `fn` inside the cross-tab refresh lock. The caller waits at most 5 s;
 * if another tab still holds the lock then, `fn` is queued to run when the
 * lock frees up, never outside it (a storage write racing a refresh's
 * check-and-write could pair one session's token with another's lineage).
 * Resolves true when `fn` already ran.
 */
async function withRefreshLockBounded(fn: () => void): Promise<boolean> {
  const locks = typeof navigator !== "undefined" ? navigator.locks : undefined;
  if (!locks) {
    fn();
    return true;
  }
  const abort = new AbortController();
  const timer = setTimeout(() => abort.abort(), LOGOUT_TIMEOUT_MS);
  try {
    await locks.request(REFRESH_LOCK, { signal: abort.signal }, async () => fn());
    return true;
  } catch {
    void locks.request(REFRESH_LOCK, async () => fn()).catch(() => {});
    return false;
  } finally {
    clearTimeout(timer);
  }
}

async function login(input: LoginInput): Promise<void> {
  const tokens = await authApi.login(input);
  await persistLogin(tokens);
  generation += 1;
  bootstrapPromise = null;
  // A refresh of the previous session may still occupy the slot; its result
  // is dropped by the generation check, and it must not block this session.
  inflightRefresh = null;
  clearRefreshTimer();
  clearCaches();
  const gen = generation;
  try {
    if (await applyTokens(tokens, gen)) scheduleSilentRefresh();
  } catch (err) {
    if (gen === generation) {
      // The login was stored before its profile failed. Undo only that: if
      // another tab already adopted and rotated it, storage is theirs now.
      generation += 1;
      bootstrapPromise = null;
      clearCaches();
      resetToAnonymous({ keepStoredToken: true });
      sessionLineage = null;
      signedOutChains.add(tokens.refresh_token);
      await withRefreshLockBounded(() => {
        if (readStoredRefresh() !== tokens.refresh_token) return;
        writeStoredRefresh(null);
        // Revoke the unused chain so it can't outlive the failed sign-in.
        void Promise.resolve()
          .then(() => authApi.logout(tokens.refresh_token))
          .catch(() => {});
      });
    }
    // The credentials were accepted; what failed is loading the account.
    if (err !== null && typeof err === "object") profileFailures.add(err);
    throw err;
  }
}

// Errors from the profile load after a successful login (not the login call).
const profileFailures = new WeakSet<object>();

/** Did this login error come after the credentials were accepted? */
export function isPostLoginFailure(error: unknown): boolean {
  return error !== null && typeof error === "object" && profileFailures.has(error);
}

const LOGOUT_TIMEOUT_MS = 5_000;

async function logout(): Promise<void> {
  // A deliberate sign-out leaves no return link to protect.
  lastSessionUser = null;
  const myLineage = sessionLineage;
  const myRefresh = state.refreshToken;
  if (myRefresh) signedOutChains.add(myRefresh);
  const storedNow = readStoredRefresh();
  const storedLineageNow = readStoredSessionId();
  const storedIsOurs = storedLineageNow !== null ? storedLineageNow === myLineage : storedNow === myRefresh;
  if (storedNow && storedIsOurs) signedOutChains.add(storedNow);
  // End the local session first: signing out must not wait on the network or
  // on another tab's refresh.
  generation += 1;
  bootstrapPromise = null;
  inflightRefresh = null;
  clearCaches();
  resetToAnonymous({ keepStoredToken: true });
  sessionLineage = null;
  signedOutVoluntarily = true;
  // Decide inside the cross-tab lock whether storage still holds our chain
  // (including a rotation a refresh just stored) or another login that
  // finished meanwhile, and only then remove it.
  const settleStorage = (): string | null => {
    const storedLineage = readStoredSessionId();
    const ours = storedLineage !== null ? storedLineage === myLineage : readStoredRefresh() === myRefresh;
    const token = (ours ? readStoredRefresh() : null) ?? myRefresh;
    if (ours) {
      markSignedOutForOtherTabs();
      writeStoredRefresh(null);
    }
    return token;
  };
  let token: string | null = null;
  let queued = false;
  const settled = await withRefreshLockBounded(() => {
    const found = settleStorage();
    // Settled late, after this tab already revoked `myRefresh`: the refresh
    // that held the lock may have rotated our chain into a new token. Revoke
    // that one too, or it stays valid after sign-out.
    if (queued && found && found !== myRefresh) {
      void Promise.resolve()
        .then(() => authApi.logout(found))
        .catch(() => {});
    }
    token = found;
  });
  // A stuck refresh holds the lock: storage is settled inside the lock once
  // it frees up; revoke the chain this tab knows right away.
  if (!settled) {
    queued = true;
    token = myRefresh;
  }
  // Then revoke server-side, best effort and bounded.
  if (token) {
    await Promise.race([
      authApi.logout(token).catch(() => {}),
      new Promise((resolve) => setTimeout(resolve, LOGOUT_TIMEOUT_MS)),
    ]);
  }
}

/** Re-read /me after a profile change (display name). */
async function reloadProfile(): Promise<void> {
  if (state.status !== "authenticated") return;
  const gen = generation;
  const me = await withDeadline(fetchMe(), PROFILE_TIMEOUT_MS);
  // Signed out (or in as someone else) while /me was in flight: drop it.
  if (gen !== generation || state.status !== "authenticated") return;
  setState({ roles: knownRoles(me.roles ?? []), me });
}

/**
 * Resolves once the session has left the loading state. `resume: false` reads
 * it without restarting a parked restore (the sign-in page must not wait on
 * another full retry cycle; signed-in pages resume it).
 */
function whenSettled({ resume = true }: { resume?: boolean } = {}): Promise<SessionSnapshot> {
  if (resume) resumeIfParked();
  if (snapshot.status !== "loading") return Promise.resolve(snapshot);
  void bootstrap();
  return new Promise((resolve) => {
    const unsubscribe = subscribeSession(() => {
      if (snapshot.status !== "loading") {
        unsubscribe();
        resolve(snapshot);
      }
    });
  });
}

export const sessionActions = {
  bootstrap,
  login,
  logout,
  clearAuthenticatedState,
  handleUnauthorized,
  ensureFreshAccessToken,
  wasSignedOutVoluntarily: () => signedOutVoluntarily,
  lastSessionUser: () => state.claims?.userId ?? lastSessionUser,
  wasRevoked,
  recoverFromUnauthorized,
  isAccessTokenUsable,
  sessionGeneration,
  reloadProfile,
  whenSettled,
};

/** Test-only reset hook. */
export function __resetSessionForTests(refreshToken: string | null = null) {
  clearRefreshTimer();
  lastRotatedByThisTab = null;
  lastSessionUser = null;
  retiredAccessToken = null;
  signedOutChains.clear();
  spentByThisTab.clear();
  bootstrapPromise = null;
  inflightRefresh = null;
  accessExpiresAt = null;
  signedOutVoluntarily = false;
  generation += 1;
  state = {
    status: refreshToken ? "loading" : "anonymous",
    accessToken: null,
    refreshToken,
    claims: null,
    roles: [],
    rolesHydrated: false,
    me: null,
  };
  snapshot = buildSnapshot(state);
}
